'use client';

import React from 'react';
import { Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VerticalLine } from '../../../types';
import { formatComparisonDate } from '../../../utils/formatters';

interface ComparisonTableProps {
  sensors: string[];
  verticalLines: VerticalLine[];
  enabledSensors: Record<string, boolean>;
  onToggleSensor: (sensorName: string) => void;
  onRemoveVerticalLine: (id: string) => void;
  onClearAllLines: () => void;
  sensorColors: Record<string, string>;
  dataTypes?: string[]; // For vibration section with multiple data types
  sectionType: string;
  onAddSensor?: (sensorName: string) => void;
  loadingSensors?: Set<string>;
  isLoading?: boolean;
  loadingLineValues?: Set<string>; // Track which vertical lines are loading values
}

export const ComparisonTable = ({
  sensors,
  verticalLines,
  enabledSensors,
  onToggleSensor,
  onRemoveVerticalLine,
  onClearAllLines,
  sensorColors,
  dataTypes = [],
  sectionType,
  onAddSensor,
  loadingSensors = new Set(),
  isLoading = false,
  loadingLineValues = new Set(),
}: ComparisonTableProps) => {
  // Debug logging
  React.useEffect(() => {
    if (verticalLines.length > 0) {
    }
  }, [verticalLines, sensors, enabledSensors]);
  
  // Smart sorting function that handles strings, numbers, and dates
  // CRITICAL: For time markers (T1, T2, M1, etc.), ALWAYS sort by timestamp, not by label
  const sortVerticalLines = React.useCallback((lines: VerticalLine[]) => {
    if (lines.length === 0) return lines;

    // ALWAYS sort by timestamp value (x-axis value)
    // This ensures chronological/numeric order regardless of label naming
    const sorted = [...lines].sort((a, b) => {
      const aTime = a.timestamp instanceof Date ? a.timestamp.getTime() : Number(a.timestamp) || 0;
      const bTime = b.timestamp instanceof Date ? b.timestamp.getTime() : Number(b.timestamp) || 0;
      return aTime - bTime;
    });

    return sorted;
  }, []);
  
  // Determine if this is a multi-chart section (like vibration with multiple data types)
  const isMultiChart = dataTypes.length > 1;

  // Check if sensor is loading
  const isSensorLoading = (sensor: string) => {
    const sensorKey = `${sectionType}-${sensor}`;
    return loadingSensors.has(sensorKey);
  };

  return (
    <div className="min-w-0 overflow-hidden relative">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-[#52606d]/50 backdrop-blur-sm z-20 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span className="text-sm font-medium text-[#01152b] dark:text-[#e1e8ed]">Loading comparison data...</span>
          </div>
        </div>
      )}
      
      <div className="flex items-center gap-2 mb-3 group">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-[#01152b] dark:text-[#e1e8ed]">Comparison Data</div>
          {verticalLines.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClearAllLines} 
              className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Clear all comparison lines"
              disabled={isLoading}
            >
              <X className="w-3 h-3 text-red-500" />
            </Button>
          )}
        </div>
      </div>

      <div className="min-w-0 overflow-x-auto">
        <table className="w-full text-xs table-auto">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 text-left p-2 min-w-fit whitespace-nowrap bg-white dark:bg-[#52606d]">
                <div className="flex items-center gap-2 font-medium text-normal dark:text-[#e1e8ed]">Select Sensors</div>
              </th>
              {verticalLines.length === 0 ? (
                <th className="text-center p-2" colSpan={6}>
                  <div className="text-xs text-[#55687d] dark:text-[#8b98a5]">Click charts to add comparison lines</div>
                </th>
              ) : (
                <>
                  {sortVerticalLines(verticalLines)
                    .slice(0, 6)
                    .map((line, index) => {
                    const dateInfo = formatComparisonDate(line.timestamp);
                    const baselineLine = verticalLines[0];
                    const isBaseline = line.id === baselineLine?.id;
                    const isLineLoading = loadingLineValues.has(line.id);
                    return (
                      <th key={line.id} className={`text-center p-2 w-[120px] ${isBaseline ? 'bg-[#F1F2F4] dark:bg-[#334155]/50' : ''} relative`}>
                        {isLineLoading && (
                          <div className="absolute inset-0 bg-white/50 dark:bg-[#52606d]/50 backdrop-blur-sm z-10 flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                          </div>
                        )}
                        <div className="group relative flex items-center justify-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${isLineLoading ? 'animate-pulse' : ''}`} style={{ backgroundColor: line.color }} />
                          <div className="min-h-[20px]">
                            <div className="text-xs whitespace-nowrap font-medium">{dateInfo.date}</div>
                            <div className="text-xs whitespace-nowrap font-medium text-[#55687D] dark:text-[#8b98a5]">{dateInfo.time}</div>
                          </div>
                          <Button
                            variant="default"
                            size="icon"
                            className="h-12 w-full absolute right-0 left-1/2 -translate-x-1/2 group-hover:opacity-100 opacity-0 !bg-primary-500 text-white  hover:bg-primary-500 rounded-lg"
                            onClick={() => onRemoveVerticalLine(line.id)}
                            disabled={isLineLoading}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </th>
                    );
                  })}
                  {/* Add empty columns to always have 6 columns */}
                  {Array.from({ length: Math.max(0, 6 - verticalLines.length) }).map((_, idx) => (
                    <th key={`empty-${idx}`} className="text-center p-2 w-[120px]">
                      <div className="text-xs text-[#55687d] dark:text-[#8b98a5] opacity-50">-</div>
                    </th>
                  ))}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sensors.map((sensor, index) => {
              const isEnabled = enabledSensors[sensor];

              if (isMultiChart) {
                // Multi-chart section (like vibration): show sensor name + data types
                return (
                  <React.Fragment key={sensor}>
                    {/* Sensor Name Row */}
                    <tr>
                      <td className="sticky left-0 z-10 p-2 border-r border-gray-100 dark:border-[#2f3844] whitespace-nowrap bg-white dark:bg-[#52606d]">
                        <button
                          onClick={() => {
                            if (isLoading) return;
                            if (isEnabled) {
                              onToggleSensor(sensor);
                            } else if (onAddSensor && !isSensorLoading(sensor)) {
                              onAddSensor(sensor);
                            }
                          }}
                          disabled={isLoading}
                          className={`flex items-center gap-2 w-full text-left rounded px-1 py-1 ${
                            isLoading ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50 dark:hover:bg-[#252b37]'
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${!isEnabled ? 'opacity-30' : ''}`}
                            style={{ backgroundColor: sensorColors[sensor] }}
                          ></div>
                          <span className={`text-sm font-medium ${isEnabled ? 'text-[#01152b] dark:text-[#e1e8ed]' : 'text-gray-400 dark:text-[#3d4653]'}`}>
                            {sensor}
                          </span>
                          {!isEnabled && onAddSensor && !isSensorLoading(sensor) && (
                            <span className="text-[10px] text-blue-600 ml-1">(load)</span>
                          )}
                          {isSensorLoading(sensor) && <Loader2 className="w-3 h-3 animate-spin text-blue-600 ml-1" />}
                        </button>
                      </td>
                      {verticalLines.length === 0 ? (
                        <td className="p-2 text-center text-xs text-[#55687d] dark:text-[#8b98a5]">-</td>
                      ) : (
                        sortVerticalLines(verticalLines)
                          .slice(0, 6)
                          .map((line) => <td key={line.id} className="p-2"></td>)
                      )}
                    </tr>

                    {/* Data Type Rows */}
                    {dataTypes.map((dataType) => (
                      <tr key={`${sensor}-${dataType}`}>
                        <td className="sticky left-0 z-10 p-2 pl-6 border-r border-gray-100 dark:border-[#2f3844] whitespace-nowrap bg-white dark:bg-[#52606d]">
                          <span className={`text-xs ${isEnabled ? 'text-[#55687d] dark:text-[#8b98a5]' : 'text-gray-400 dark:text-[#3d4653]'}`}>
                            {dataType}
                          </span>
                        </td>
                        {verticalLines.length === 0 ? (
                          <td className="p-2 text-center text-xs text-[#55687d] dark:text-[#8b98a5]">-</td>
                        ) : (
                          sortVerticalLines(verticalLines)
                            .slice(0, 6)
                            .map((line, lineIndex) => {
                              // Try to find the value with different key variations
                              let value = 0;
                              if (line.values) {
                                // Try exact match first
                                if (sensor in line.values) {
                                  value = line.values[sensor];
                                } else {
                                  // Try to find a matching key (case-insensitive or with slight variations)
                                  const keys = Object.keys(line.values);
                                  const matchingKey = keys.find(key => 
                                    key === sensor || 
                                    key.toLowerCase() === sensor.toLowerCase() ||
                                    key.replace(/\s+/g, '') === sensor.replace(/\s+/g, '')
                                  );
                                  if (matchingKey) {
                                    value = line.values[matchingKey];
                                  } else if (lineIndex === 0) {
                                  }
                                }
                              } else if (lineIndex === 0) {
                              }
                              const baselineLine = verticalLines[0];
                              const baseValue = baselineLine?.values?.[sensor] || 0;
                              const isBaseline = line.id === verticalLines[0]?.id;
                              const percentDiff = isBaseline
                                ? 0
                                : baseValue !== 0
                                ? ((value - baseValue) / baseValue) * 100
                                : 0;

                              const isLineLoading = loadingLineValues.has(line.id);
                              const hasNoValue = !line.values || !(sensor in line.values);
                              
                              return (
                                <td
                                  key={line.id}
                                  className={`text-center p-2 ${isBaseline ? 'bg-[#F1F2F4] dark:bg-[#334155]/50' : ''} ${
                                    !isEnabled ? 'opacity-40' : ''
                                  }`}
                                >
                                  <div className="font-mono text-xs leading-tight">
                                    {isSensorLoading(sensor) || isLineLoading ? (
                                      <Loader2 className="w-3 h-3 animate-spin mx-auto text-blue-600" />
                                    ) : hasNoValue ? (
                                      <div className="text-gray-400 dark:text-gray-500">-</div>
                                    ) : isBaseline ? (
                                      <div>{value !== undefined ? value.toFixed(1) : '0.0'}</div>
                                    ) : (
                                      <>
                                        <div>{value !== undefined ? value.toFixed(1) : '0.0'}</div>
                                        <div className="text-[10px] opacity-75">
                                          {percentDiff >= 0 ? '+' : ''}
                                          {percentDiff.toFixed(1)}%
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </td>
                              );
                            })
                        )}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              } else {
                // Single-chart section: show sensor name with values directly in the same row
                return (
                  <tr key={sensor}>
                    <td className="sticky left-0 z-10 p-2 border-r border-gray-100 dark:border-[#2f3844] whitespace-nowrap bg-white dark:bg-[#52606d]">
                      <button
                        onClick={() => {
                          if (isLoading) return;
                          if (isEnabled) {
                            onToggleSensor(sensor);
                          } else if (onAddSensor && !isSensorLoading(sensor)) {
                            onAddSensor(sensor);
                          }
                        }}
                        disabled={isLoading}
                        className={`flex items-center gap-2 w-full text-left rounded px-1 py-1 ${
                          isLoading ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50 dark:hover:bg-[#252b37]'
                        }`}
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${!isEnabled ? 'opacity-30' : ''}`}
                          style={{ backgroundColor: sensorColors[sensor] }}
                        ></div>
                        <span className={`text-sm font-medium ${isEnabled ? 'text-[#01152b] dark:text-[#e1e8ed]' : 'text-gray-400 dark:text-[#3d4653]'}`}>
                          {sensor}
                        </span>
                        {!isEnabled && onAddSensor && !isSensorLoading(sensor) && (
                          <span className="text-[10px] text-blue-600 ml-1">(load)</span>
                        )}
                        {isSensorLoading(sensor) && <Loader2 className="w-3 h-3 animate-spin text-blue-600 ml-1" />}
                      </button>
                    </td>
                    {verticalLines.length === 0 ? (
                      <td className="p-2 text-center text-xs text-[#55687d] dark:text-[#8b98a5]">-</td>
                    ) : (
                      sortVerticalLines(verticalLines)
                        .slice(0, 6)
                        .map((line, lineIndex) => {
                          // Try to find the value with different key variations
                          let value = 0;
                          if (line.values) {
                            // Try exact match first
                            if (sensor in line.values) {
                              value = line.values[sensor];
                            } else {
                              // Try to find a matching key (case-insensitive or with slight variations)
                              const keys = Object.keys(line.values);
                              const matchingKey = keys.find(key => 
                                key === sensor || 
                                key.toLowerCase() === sensor.toLowerCase() ||
                                key.replace(/\s+/g, '') === sensor.replace(/\s+/g, '')
                              );
                              if (matchingKey) {
                                value = line.values[matchingKey];
                              } else if (lineIndex === 0) {
                              }
                            }
                          } else if (lineIndex === 0) {
                          }
                          const baselineLine = verticalLines[0];
                          const baseValue = baselineLine?.values?.[sensor] || 0;
                          const isBaseline = line.id === verticalLines[0]?.id;
                          const percentDiff = isBaseline
                            ? 0
                            : baseValue !== 0
                            ? ((value - baseValue) / baseValue) * 100
                            : 0;

                          const isLineLoading = loadingLineValues.has(line.id);
                          const hasNoValue = !line.values || !(sensor in line.values);
                          
                          return (
                            <td
                              key={line.id}
                              className={`text-center p-2 ${isBaseline ? 'bg-[#F1F2F4] dark:bg-[#334155]/50' : ''} ${
                                !isEnabled ? 'opacity-40' : ''
                              }`}
                            >
                              <div className="font-mono text-xs leading-tight">
                                {isSensorLoading(sensor) || isLineLoading ? (
                                  <Loader2 className="w-3 h-3 animate-spin mx-auto text-blue-600" />
                                ) : hasNoValue ? (
                                  <div className="text-gray-400 dark:text-gray-500">-</div>
                                ) : isBaseline ? (
                                  <div>{value !== undefined ? value.toFixed(1) : '0.0'}</div>
                                ) : (
                                  <>
                                    <div>{value !== undefined ? value.toFixed(1) : '0.0'}</div>
                                    <div className="text-[10px] opacity-75">
                                      {percentDiff >= 0 ? '+' : ''}
                                      {percentDiff.toFixed(1)}%
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        })
                    )}
                  </tr>
                );
              }
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
